import { environment } from '../../environments/environment';

// Generic logging utility for the application
// 
// Log levels:
// - debug: Verbose diagnostic information (only in development, suppressed in tests)
// - info: Important events and state changes (always logged)
// - warn: Warnings and issues (always logged)
// - error: Errors and failures (always logged)
export class Logger {
  constructor(private prefix: string) { }

  // Check if we're running in a test environment
  private isTestEnvironment(): boolean {
    // Check for Jasmine (used by Karma tests)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return typeof (globalThis as any).jasmine !== 'undefined';
  }

  // Debug-level logging - only in development, suppressed in tests
  // Use for verbose diagnostic information
  debug(message: string, ...args: unknown[]): void {
    if (!environment.production && !this.isTestEnvironment()) {
      console.debug(`${this.prefix} ${message}`, ...args);
    }
  }

  // Info-level logging - always logged
  // Use for important events and state changes
  info(message: string, ...args: unknown[]): void {
    console.log(`${this.prefix} ${message}`, ...args);
  }

  // Warn-level logging - always logged
  // Use for warnings and issues
  warn(message: string, ...args: unknown[]): void {
    console.warn(`${this.prefix} ${message}`, ...args);
  }

  // Error-level logging - always logged
  // Use for errors and failures
  error(message: string, error?: unknown, ...args: unknown[]): void {
    console.error(`${this.prefix} ${message}`, error, ...args);
  }

  // Create a scoped logger with an event ID for tracking related logs
  scoped(eventId: string): {
    debug: (message: string, ...args: unknown[]) => void;
    info: (message: string, ...args: unknown[]) => void;
    warn: (message: string, ...args: unknown[]) => void;
    error: (message: string, error?: unknown, ...args: unknown[]) => void;
  } {
    return {
      debug: (message: string, ...args: unknown[]) =>
        this.debug(`[${eventId}] ${message}`, ...args),
      info: (message: string, ...args: unknown[]) =>
        this.info(`[${eventId}] ${message}`, ...args),
      warn: (message: string, ...args: unknown[]) =>
        this.warn(`[${eventId}] ${message}`, ...args),
      error: (message: string, error?: unknown, ...args: unknown[]) =>
        this.error(`[${eventId}] ${message}`, error, ...args),
    };
  }
}

// Pre-configured logger instances for common use cases
export const SilentRefreshLogger = new Logger('*** [SilentRefresh]');

